/**
 * MessageStatus - Indicador visual de status de mensagem
 *
 * Exibe ícones de status para mensagens enviadas:
 * - sent: ✓ (cinza) - Mensagem enviada
 * - delivered: ✓✓ (cinza) - Entregue ao destinatário
 * - read: ✓✓ (azul) - Lida pelo destinatário
 *
 * Apenas exibido para mensagens próprias (direction: 'outbound')
 *
 * @example
 * <MessageStatus status="read" timestamp="2025-10-01T10:30:00Z" />
 */

import React from 'react';
import { Icon } from '@/shared/icons'
import { Tooltip } from '@/shared/components/ui';
export type MessageStatusType = 'sent' | 'delivered' | 'read' | 'failed' | 'pending';

interface MessageStatusProps {
  status?: MessageStatusType;
  timestamp?: string;
  showTimestamp?: boolean;
  isAI?: boolean;
  isAutomation?: boolean;
}

const MessageStatus: React.FC<MessageStatusProps> = ({
  status = 'sent',
  timestamp,
  showTimestamp = true,
  isAI = false,
  isAutomation = false,
}) => {
  /**
   * Formatar timestamp para exibição
   */
  const formatTime = (isoString: string): string => {
    const date = new Date(isoString);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  /**
   * Cor do ícone por status (consome CSS vars do tema chat)
   */
  const getIconStyle = (): React.CSSProperties => {
    switch (status) {
      case 'read':
        return { color: 'var(--chat-ticks-read)' };
      case 'sent':
      case 'delivered':
        return { color: 'var(--chat-ticks-sent)' };
      case 'failed':
        return { color: '#ef4444' };
      default:
        return { color: 'currentColor', opacity: 0.6 };
    }
  };

  /**
   * Renderizar ícone de status baseado no estado
   */
  const renderStatusIcon = () => {
    const iconStyle = getIconStyle();
    switch (status) {
      case 'sent':
        // 1 check
        return (
          <span style={iconStyle} data-testid="chat-message-status-sent">
            <Icon name="check" className="w-4 h-4" />
          </span>
        );

      case 'delivered':
        // 2 checks (padrão WhatsApp)
        return (
          <span style={iconStyle} data-testid="chat-message-status-delivered">
            <div className="relative w-4 h-4">
              <Icon name="check" className="w-4 h-4 absolute" style={{ left: '-2px' }} />
              <Icon name="check" className="w-4 h-4 absolute" style={{ left: '2px' }} />
            </div>
          </span>
        );

      case 'read':
        // 2 checks lidos (padrão WhatsApp — azul/colorido)
        return (
          <span style={iconStyle} data-testid="chat-message-status-read">
            <div className="relative w-4 h-4">
              <Icon name="check" className="w-4 h-4 absolute" style={{ left: '-2px' }} />
              <Icon name="check" className="w-4 h-4 absolute" style={{ left: '2px' }} />
            </div>
          </span>
        );

      case 'failed':
        // Tooltip shared (F80) — title= nativo não é mais usado
        return (
          <Tooltip content="Falha ao enviar">
            <span
              className="text-xs font-medium inline-flex"
              style={{ ...iconStyle, pointerEvents: 'auto' }}
              data-testid="chat-message-status-failed"
            >
              !
            </span>
          </Tooltip>
        );

      case 'pending':
        return (
          <span style={iconStyle} data-testid="chat-message-status-pending">
            <div className="w-4 h-4 flex items-center justify-center">
              <div className="w-2 h-2 rounded-full bg-gray-300 animate-pulse"></div>
            </div>
          </span>
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex items-center gap-1 text-xs text-gray-500">
      {isAI && (
        <Tooltip content="Enviada por inteligência artificial">
          <span className="inline-flex" style={{ pointerEvents: 'auto' }} data-testid="chat-message-ai-indicator">
            <Icon name="cpu" className="w-3.5 h-3.5 text-indigo-400"
              style={{ cursor: 'pointer', display: 'block' }} />
          </span>
        </Tooltip>
      )}
      {isAutomation && (
        <Tooltip content="Enviado por um workflow">
          <span className="inline-flex" style={{ pointerEvents: 'auto' }} data-testid="chat-message-automation-indicator">
            <Icon name="zap" className="w-3.5 h-3.5 text-indigo-400"
              style={{ cursor: 'pointer', display: 'block' }} />
          </span>
        </Tooltip>
      )}
      {showTimestamp && timestamp && (
        <span className="text-gray-400">{formatTime(timestamp)}</span>
      )}
      {renderStatusIcon()}
    </div>
  );
};

export default MessageStatus;
